import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthAPI } from '../services/api';
import { PasswordField } from '../components/PasswordField';
import { LoadingScreen } from '../components/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { usernameValidation, passwordValidation } from '../schemas/validation';
import '../components/AuthForms.css';

const inviteSignupSchema = z.object({
  username: usernameValidation,
  password: passwordValidation,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  full_name: z.string().min(1, 'Full name is required'),
  email: z.string().email('Valid email is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type InviteSignupData = z.infer<typeof inviteSignupSchema>;

export const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [inviteToken, setInviteToken] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<InviteSignupData>({
    resolver: zodResolver(inviteSignupSchema),
  });

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid invite link - no token provided');
      setLoading(false);
      return;
    }
    
    setInviteToken(token);
    
    // Validate token and get email
    const validateToken = async () => {
      try {
        const inviteInfo = await AuthAPI.validateInviteToken(token);
        setInviteEmail(inviteInfo.email);
        setValue('email', inviteInfo.email);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Invalid invite token');
        setLoading(false);
      }
    };
    
    validateToken();
  }, [searchParams, setValue]);


  const onSubmit = async (data: InviteSignupData) => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);

      // Logout any existing user first
      await logout();

      // Remove confirmPassword from the signup data
      const { confirmPassword, ...signupData } = data;
      
      // Add invite token to signup data
      const finalSignupData = {
        ...signupData,
        invite_token: inviteToken,
      };

      await AuthAPI.signup(finalSignupData);
      setSuccess('Account created successfully! You can now login with your credentials.');

      reset();
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Account creation failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !inviteToken) {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading invitation...
          </div>
        </div>
      </div>
    );
  }

  if (error && !inviteToken) {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <h2>Invalid Invitation</h2>
          <div className="error-message">{error}</div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button 
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form-container">
      <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <h2>Complete Your Registration</h2>
        <p className="info-text">
          You've been invited to join the Affordable Connects platform. 
          Please fill in the details below to create your account.
        </p>
        
        {error && <div className="error-message">{error}</div>}
        {success && (
          <div className="success-message">
            {success}
            <div style={{ marginTop: '1rem' }}>
              <button 
                onClick={() => navigate('/login')}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem' }}
              >
                Go to Login
              </button>
            </div>
          </div>
        )}

        <div style={{ 
          padding: '1rem', 
          background: 'var(--bg-secondary)', 
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '1px solid var(--border-primary)'
        }}>
          <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            <strong>📧 Email:</strong> {inviteEmail || 'Loading from invitation...'}<br/>
            <strong>🔒 Security:</strong> 2FA is automatically enabled for all accounts
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email (from invitation)</label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={errors.email ? 'error' : ''}
            placeholder="Loading email from invitation..."
            value={inviteEmail}
            readOnly
            disabled={true}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              cursor: 'not-allowed'
            }}
          />
          {errors.email && <span className="field-error">{errors.email.message}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="full_name">Full Name</label>
          <input
            id="full_name"
            type="text"
            {...register('full_name')}
            className={errors.full_name ? 'error' : ''}
            placeholder="Enter your full name"
            disabled={loading}
          />
          {errors.full_name && <span className="field-error">{errors.full_name.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            {...register('username')}
            className={errors.username ? 'error' : ''}
            placeholder="Choose a username"
            disabled={loading}
          />
          {errors.username && <span className="field-error">{errors.username.message}</span>}
        </div>

        <PasswordField
          id="password"
          label="Password"
          placeholder="Create a strong password"
          register={register}
          error={errors.password?.message}
          disabled={loading}
          showRequirements={true}
        />

        <PasswordField
          id="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm your password"
          register={register}
          error={errors.confirmPassword?.message}
          disabled={loading}
        />

        <button 
          type="submit" 
          disabled={loading}
          className="btn btn-primary submit-button"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>

        <div style={{ 
          marginTop: '1rem', 
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--text-muted)'
        }}>
          <p>Already have an account? <button type="button" onClick={() => navigate('/login')} className="btn btn-ghost">Login here</button></p>
        </div>
      </form>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Completing registration..." />
    </div>
  );
};